import type { KnockStatusDetailsDto, LocationDTO } from '@sales-center/common'

const DO_NOT_KNOCK = 'Do not knock'

export type MostRecentKnockStatusValue = {
  status: string
  reason: string | null
  isDefault: boolean
}

const isMeaningfulStatus = (
  status: string | null | undefined,
): status is string => {
  const trimmed = status?.trim()
  return !!trimmed && trimmed !== 'None'
}

const toValue = (
  status: string,
  reason: string | null | undefined,
): MostRecentKnockStatusValue => ({
  status,
  reason: reason && reason.trim() ? reason : null,
  isDefault: status === DO_NOT_KNOCK,
})

/**
 * Resolve the knock status from the card-level string fields the OData card
 * endpoints expose (`knockStatus` is the most-recent active status name across
 * the entity's service locations, `knockStatusReason` is its note).
 */
export function getKnockStatusFromCard(
  status: string | null | undefined,
  reason: string | null | undefined,
): MostRecentKnockStatusValue | null {
  return isMeaningfulStatus(status) ? toValue(status.trim(), reason) : null
}

/**
 * Pick the most recently added knock status across an entity's service
 * locations (detail view). When multiple service locations carry a knock
 * status, the one with the latest `createdTime` wins.
 */
export function getMostRecentKnockStatusFromLocations(
  serviceLocations: LocationDTO[] | null | undefined,
): MostRecentKnockStatusValue | null {
  if (!serviceLocations?.length) {
    return null
  }

  const details = serviceLocations
    .map((location) => location.knockStatusDetails)
    .filter(
      (detail): detail is KnockStatusDetailsDto =>
        !!detail && isMeaningfulStatus(detail.knockStatusSettingsName),
    )

  if (!details.length) {
    return null
  }

  const mostRecent = details.reduce((latest, current) =>
    new Date(current.createdTime).getTime() >
    new Date(latest.createdTime).getTime()
      ? current
      : latest,
  )

  return toValue(
    mostRecent.knockStatusSettingsName.trim(),
    mostRecent.mostRecentNote,
  )
}
